import React, { FC, useMemo } from 'react';
import { Box } from '~Styles';
import { useI18n, useSelector } from '~Hooks';
import {
  CompleteBongQuery_venue_company_items,
  CompleteBongQuery_venue_company_modificationGroups,
} from '~Scenes/POS/Queries/__queries__';
import { useInventoryApi, useInventoryHelpers } from '~Components/InventoryUniverse';
import VariationRow from './VariationRow';

interface UniqueVariation {
  id: string;
  name: string;
  nameLang: any;
  productNames: string[];
}

interface Props {
  items: CompleteBongQuery_venue_company_items[];
  modificationGroups: CompleteBongQuery_venue_company_modificationGroups[];
  search?: string;
}

const Variations: FC<Props> = ({ items, modificationGroups, search = '' }) => {
  const { i18n } = useI18n();
  const venueId = useSelector((state) => state.venueId);
  const inventoryApi = useInventoryApi(venueId);
  const { itemIsOutOfStock } = useInventoryHelpers();

  // Flatten all variations from modification groups and deduplicate by id
  // Also count how many products use each variation via their modificationGroups
  const uniqueVariations = useMemo(() => {
    // Build a map: variation ID → set of modificationGroup IDs it belongs to
    const variationToGroups = new Map<string, Set<string>>();

    const seen = new Set<string>();
    const variations: Omit<UniqueVariation, 'productNames'>[] = [];

    for (const group of modificationGroups) {
      for (const variation of group.variations) {
        // Skip allergens, same as Ingredients.tsx
        if (variation.allergen) continue;

        // Track which groups this variation belongs to
        if (!variationToGroups.has(variation.id)) {
          variationToGroups.set(variation.id, new Set());
        }
        variationToGroups.get(variation.id)!.add(group.id);

        if (!seen.has(variation.id)) {
          seen.add(variation.id);
          variations.push({
            id: variation.id,
            name: variation.name,
            nameLang: variation.nameLang,
          });
        }
      }
    }

    // Collect product names per variation: a product uses a variation if it references
    // any modificationGroup that contains that variation
    return variations
      .map((variation) => {
        const groupIds = variationToGroups.get(variation.id) ?? new Set();
        const productNames = items
          .filter((item) => item.modificationGroups.some((mgId) => groupIds.has(mgId)))
          .map((item) => i18n(item.titleLang));

        return { ...variation, productNames };
      })
      .filter((v) => {
        if (!search) return true;
        return i18n(v.nameLang).toLowerCase().includes(search.toLowerCase());
      })
      .sort((a, b) => {
        const nameA = i18n(a.nameLang).toLowerCase();
        const nameB = i18n(b.nameLang).toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [modificationGroups, items, i18n, search]);

  return (
    <Box>
      {uniqueVariations.map((variation) => {
        const isOutOfStock = itemIsOutOfStock(variation.id);

        return (
          <VariationRow
            key={variation.id}
            name={i18n(variation.nameLang)}
            productNames={variation.productNames}
            isOutOfStock={isOutOfStock}
            onToggleStock={() => inventoryApi.setOutOfStock(variation.id, !isOutOfStock)}
            i18n={i18n}
          />
        );
      })}
    </Box>
  );
};

export default Variations;
